import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { href: '/', label: 'Ana Sayfa' },
    { href: '/hakkimizda', label: 'Hakkımızda' },
    { href: '/hizmetler', label: 'Hizmetlerimiz' },
    { href: '/projeler', label: 'Projelerimiz' },
    { href: '/surec', label: 'Süreç' },
    { href: '/iletisim', label: 'İletişim' },
  ];

  const isHome = location.pathname === '/';
  const isLight = isScrolled || !isHome;

  const isActive = (href) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  return (
    <header
      data-testid="main-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-700 ${
        isScrolled
          ? 'py-3 bg-[#f2f2f7]/98 backdrop-blur-xl shadow-sm'
          : 'py-5 bg-transparent'
      }`}
    >
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
        <nav className="flex items-center justify-between">
          {/* Logo */}
          <Link
            to="/"
            data-testid="header-logo"
            className="relative z-10"
          >
            <img
              src="/logo-vepera.png"
              alt="VE PERA İnşaat"
              className={`h-10 lg:h-12 w-auto transition-all duration-500 ${
                isLight ? '' : 'brightness-0 invert'
              }`}
            />
          </Link>

          {/* Desktop Navigation - Centered */}
          <div className="hidden lg:flex items-center absolute left-1/2 transform -translate-x-1/2">
            <div className="flex items-center gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  data-testid={`nav-${link.href.replace('/', '') || 'home'}`}
                  className={`relative px-5 py-2 text-[12px] tracking-[0.04em] transition-all duration-300 ${
                    isActive(link.href)
                      ? 'text-[#041f60]'
                      : isLight
                        ? 'text-[#1a1a1a]/70 hover:text-[#1a1a1a]'
                        : 'text-[#f2f2f7]/70 hover:text-[#f2f2f7]'
                  }`}
                  style={{ fontFamily: "var(--font-body)", fontWeight: 500 }}
                >
                  {link.label}
                  {isActive(link.href) && (
                    <span className="absolute bottom-0 left-5 right-5 h-[1px] bg-[#041f60]"></span>
                  )}
                </Link>
              ))}
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`lg:hidden relative z-10 p-2 ${isLight ? 'text-[#1a1a1a]' : 'text-[#f2f2f7]'}`}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </nav>

        {/* Mobile Navigation */}
        <div
          data-testid="mobile-menu"
          className={`lg:hidden fixed inset-0 bg-[#f2f2f7] transition-all duration-500 ${
            isMobileMenuOpen 
              ? 'opacity-100 pointer-events-auto' 
              : 'opacity-0 pointer-events-none'
          }`}
          style={{ top: '0', zIndex: 40 }}
        >
          <div className="flex flex-col justify-center h-full px-8">
            <div className="space-y-1">
              {navLinks.map((link, index) => (
                <Link
                  key={link.href}
                  to={link.href}
                  data-testid={`mobile-nav-${link.href.replace('/', '') || 'home'}`}
                  className={`block py-4 text-[32px] tracking-[-0.02em] transition-all duration-300 ${
                    isActive(link.href)
                      ? 'text-[#041f60]'
                      : 'text-[#1a1a1a]'
                  }`}
                  style={{ 
                    fontFamily: "var(--font-heading)", 
                    fontWeight: 400,
                    transitionDelay: `${index * 50}ms`
                  }}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
